import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

/**
 * Landing pública — www.pillarmoney.com (raiz do payflow-web).
 *
 * Direção visual:
 *  - Dark-theme forçado (a landing tem branding próprio, independente do tema do usuário).
 *  - Paleta: navy tinta (#08101E), verde elétrico (#00C853) como único destaque cromático.
 *  - Tipografia: Bricolage Grotesque (display) + Manrope (body) — carregadas via <link> dinâmico.
 *  - Mockups inline (SVG + divs) — zero dependência externa de imagem.
 */

export function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const goCheckout = (plan: string) => navigate(`/checkout?plan=${plan}`);

  // Carrega Bricolage Grotesque + Manrope
  useEffect(() => {
    const id = "landing-fonts";
    if (document.getElementById(id)) return;
    const preconnect1 = document.createElement("link");
    preconnect1.rel = "preconnect";
    preconnect1.href = "https://fonts.googleapis.com";
    preconnect1.id = id;
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement("link");
    preconnect2.rel = "preconnect";
    preconnect2.href = "https://fonts.gstatic.com";
    preconnect2.crossOrigin = "anonymous";
    document.head.appendChild(preconnect2);
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500..800&family=Manrope:wght@300..700&display=swap";
    document.head.appendChild(link);
    return () => {
      // não remove — pode ser reutilizado em outras páginas sem re-baixar
    };
  }, []);

  return (
    <div
      className="min-h-screen"
      style={{
        background: "#06101F",
        fontFamily: '"Manrope", system-ui, -apple-system, sans-serif',
        color: "#E2E8F0",
        // garante overrides locais sobre qualquer tema do app
        ["--lg-bg" as any]: "#06101F",
        ["--lg-surface" as any]: "#0C1628",
        ["--lg-surface-2" as any]: "#111D33",
        ["--lg-border" as any]: "rgba(148, 163, 184, 0.12)",
        ["--lg-border-strong" as any]: "rgba(148, 163, 184, 0.22)",
        ["--lg-green" as any]: "#00C853",
        ["--lg-green-hover" as any]: "#00E676",
        ["--lg-text" as any]: "#F8FAFC",
        ["--lg-text-2" as any]: "#CBD5E1",
        ["--lg-muted" as any]: "#64748B",
      }}
    >
      <StyleTag />

      {/* ========== HEADER ========== */}
      <Header t={t} />

      {/* ========== HERO ========== */}
      <HeroSection t={t} goCheckout={goCheckout} />

      {/* ========== STEPS ========== */}
      <StepsSection t={t} />

      {/* ========== STATS + TESTIMONIAL ========== */}
      <ResultsSection t={t} />

      {/* ========== PRICING ========== */}
      <PricingSection t={t} goCheckout={goCheckout} />

      {/* ========== FAQ ========== */}
      <FaqSection t={t} openFaq={openFaq} setOpenFaq={setOpenFaq} />

      {/* ========== FINAL CTA ========== */}
      <FinalCtaSection t={t} goCheckout={goCheckout} />

      {/* ========== FOOTER ========== */}
      <Footer t={t} />
    </div>
  );
}

// ==================== STYLE (fonts + animations + grid pattern) ====================

function StyleTag() {
  return (
    <style>{`
      .lg-display {
        font-family: "Bricolage Grotesque", "Manrope", system-ui, sans-serif;
        font-optical-sizing: auto;
        letter-spacing: -0.02em;
      }
      .lg-mono {
        font-feature-settings: "tnum" 1, "ss01" 1;
        font-variant-numeric: tabular-nums;
      }
      .lg-dot-grid {
        background-image: radial-gradient(rgba(148, 163, 184, 0.14) 1px, transparent 1px);
        background-size: 28px 28px;
      }
      .lg-hero-glow {
        background:
          radial-gradient(ellipse 60% 50% at 50% 40%, rgba(0, 200, 83, 0.12), transparent 60%),
          radial-gradient(ellipse 40% 30% at 20% 80%, rgba(0, 200, 83, 0.06), transparent 50%),
          radial-gradient(ellipse 30% 40% at 85% 20%, rgba(74, 222, 128, 0.05), transparent 50%);
      }
      .lg-card {
        background: linear-gradient(180deg, rgba(148, 163, 184, 0.04) 0%, rgba(148, 163, 184, 0.01) 100%), #0C1628;
        border: 1px solid rgba(148, 163, 184, 0.12);
        transition: transform 300ms cubic-bezier(.2,.8,.2,1), border-color 300ms, box-shadow 300ms;
      }
      .lg-card:hover {
        transform: translateY(-3px);
        border-color: rgba(0, 200, 83, 0.35);
        box-shadow: 0 22px 50px -12px rgba(0, 200, 83, 0.10);
      }
      .lg-cta {
        background: linear-gradient(180deg, #00E676 0%, #00C853 100%);
        color: #0A0F1A;
        font-weight: 700;
        box-shadow: 0 10px 30px -10px rgba(0, 200, 83, 0.5), inset 0 1px 0 rgba(255,255,255,0.22);
        transition: transform 150ms cubic-bezier(.2,.8,.2,1), box-shadow 200ms;
      }
      .lg-cta:hover {
        transform: translateY(-1px);
        box-shadow: 0 16px 40px -10px rgba(0, 200, 83, 0.6), inset 0 1px 0 rgba(255,255,255,0.25);
      }
      .lg-cta:active { transform: translateY(0); }
      .lg-ghost {
        background: transparent;
        border: 1px solid rgba(148, 163, 184, 0.22);
        color: #E2E8F0;
        transition: border-color 200ms, background 200ms;
      }
      .lg-ghost:hover {
        border-color: rgba(0, 200, 83, 0.55);
        background: rgba(0, 200, 83, 0.06);
      }
      .lg-reveal {
        opacity: 0;
        transform: translateY(18px);
        animation: lg-reveal-in 900ms cubic-bezier(.2,.8,.2,1) forwards;
      }
      .lg-reveal-d1 { animation-delay: 80ms; }
      .lg-reveal-d2 { animation-delay: 180ms; }
      .lg-reveal-d3 { animation-delay: 280ms; }
      .lg-reveal-d4 { animation-delay: 380ms; }
      .lg-reveal-d5 { animation-delay: 480ms; }
      .lg-reveal-d6 { animation-delay: 580ms; }
      @keyframes lg-reveal-in {
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes lg-pulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(0, 200, 83, 0.45); }
        70% { box-shadow: 0 0 0 12px rgba(0, 200, 83, 0); }
      }
      .lg-live-dot {
        animation: lg-pulse 2s infinite;
      }
      .lg-underline-accent {
        position: relative;
        display: inline-block;
      }
      .lg-underline-accent::after {
        content: "";
        position: absolute;
        left: 0; right: 0; bottom: -4px;
        height: 10px;
        background: linear-gradient(90deg, rgba(0,200,83,0.0) 0%, rgba(0,200,83,0.35) 25%, rgba(0,200,83,0.35) 75%, rgba(0,200,83,0.0) 100%);
        filter: blur(6px);
        z-index: -1;
      }
      .lg-scrollbar-hide::-webkit-scrollbar { display: none; }
      .lg-scrollbar-hide { scrollbar-width: none; }
    `}</style>
  );
}

// ==================== HEADER ====================

function Header({ t }: { t: (k: string) => string }) {
  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl"
      style={{
        background: "rgba(6, 16, 31, 0.72)",
        borderBottom: "1px solid var(--lg-border)",
      }}
    >
      <div className="mx-auto flex h-18 max-w-7xl items-center justify-between px-5 py-4 lg:px-10">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5">
          <LogoMark />
          <span className="lg-display text-[1.35rem] font-bold tracking-tight" style={{ color: "var(--lg-text)" }}>
            Cred<span style={{ color: "var(--lg-green)" }}>ix</span>
          </span>
        </Link>

        {/* Nav */}
        <nav className="hidden items-center gap-8 text-[0.95rem] lg:flex" style={{ color: "var(--lg-text-2)" }}>
          <a href="#features" className="transition-colors hover:text-white">
            {t("landing.nav_features")}
          </a>
          <a href="#how-it-works" className="transition-colors hover:text-white">
            {t("landing.nav_how")}
          </a>
          <a href="#benefits" className="transition-colors hover:text-white">
            {t("landing.nav_benefits")}
          </a>
          <a href="#testimonials" className="transition-colors hover:text-white">
            {t("landing.nav_testimonials")}
          </a>
          <a href="#pricing" className="transition-colors hover:text-white">
            {t("landing.nav_pricing")}
          </a>
          <a href="#faq" className="transition-colors hover:text-white">
            {t("landing.nav_faq")}
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/login"
            className="hidden rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:text-white lg:inline-block"
            style={{ color: "var(--lg-text-2)" }}
          >
            {t("auth.login_button")}
          </Link>
          <a
            href="#pricing"
            className="lg-cta inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm"
          >
            {t("landing.nav_cta")}
            <ArrowRightIcon className="h-4 w-4" />
          </a>
        </div>
      </div>
    </header>
  );
}

function LogoMark() {
  // Ícone geométrico C — evoca "Crédito · Controle · Cobrança"
  return (
    <div
      className="flex h-10 w-10 items-center justify-center rounded-xl"
      style={{
        background: "linear-gradient(135deg, #00E676 0%, #00A844 100%)",
        boxShadow: "0 6px 20px -6px rgba(0, 200, 83, 0.5), inset 0 1px 0 rgba(255,255,255,0.25)",
      }}
    >
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
          d="M17 6.5C15.85 4.4 13.6 3 11 3C7.13 3 4 6.13 4 10C4 13.87 7.13 17 11 17C13.6 17 15.85 15.6 17 13.5"
          stroke="#0A0F1A"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="15.5" cy="10" r="1.4" fill="#0A0F1A" />
      </svg>
    </div>
  );
}

// ==================== HERO ====================

function HeroSection({ t, goCheckout }: { t: (k: string) => string; goCheckout: (plan: string) => void }) {
  return (
    <section className="relative overflow-hidden">
      <div className="lg-hero-glow absolute inset-0" />
      <div className="lg-dot-grid absolute inset-0 opacity-50" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 pb-16 pt-14 lg:grid-cols-[1.05fr,1fr] lg:gap-10 lg:px-10 lg:pb-28 lg:pt-20">
        {/* ===== LEFT: COPY ===== */}
        <div className="lg-reveal lg-reveal-d1">
          {/* Badge */}
          <div
            className="mb-7 inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[0.72rem] font-semibold uppercase tracking-[0.12em]"
            style={{
              background: "rgba(0, 200, 83, 0.08)",
              borderColor: "rgba(0, 200, 83, 0.30)",
              color: "#4ADE80",
            }}
          >
            <CheckIcon className="h-3.5 w-3.5" style={{ color: "var(--lg-green)" }} />
            {t("landing.hero_badge_new")}
          </div>

          {/* Headline */}
          <h1
            className="lg-display mb-5 text-[2.6rem] font-extrabold leading-[1.02] tracking-tight sm:text-[3.2rem] lg:text-[3.9rem]"
            style={{ color: "var(--lg-text)" }}
          >
            {t("landing.hero_headline_part1")}
            <br />
            <span className="lg-underline-accent" style={{ color: "var(--lg-green)" }}>
              {t("landing.hero_headline_highlight")}
            </span>
          </h1>

          {/* Subtítulo */}
          <p className="mb-4 text-[1.15rem] font-medium lg:text-[1.25rem]" style={{ color: "var(--lg-text)" }}>
            {t("landing.hero_subtitle_new")}
          </p>

          {/* Descrição */}
          <p className="mb-8 max-w-[32rem] text-[0.98rem] leading-relaxed" style={{ color: "var(--lg-text-2)" }}>
            {t("landing.hero_description_long")}
          </p>

          {/* Mini features grid */}
          <div
            className="mb-8 grid gap-3 rounded-2xl border p-3 sm:grid-cols-2 lg:grid-cols-4"
            style={{
              background: "rgba(12, 22, 40, 0.6)",
              borderColor: "var(--lg-border)",
            }}
          >
            <MiniFeature
              icon={<ShieldCheckIcon className="h-5 w-5" />}
              title={t("landing.hero_feature_1_title")}
              desc={t("landing.hero_feature_1_desc")}
            />
            <MiniFeature
              icon={<TrendDownIcon className="h-5 w-5" />}
              title={t("landing.hero_feature_2_title")}
              desc={t("landing.hero_feature_2_desc")}
            />
            <MiniFeature
              icon={<ClockIcon className="h-5 w-5" />}
              title={t("landing.hero_feature_3_title")}
              desc={t("landing.hero_feature_3_desc")}
            />
            <MiniFeature
              icon={<PhoneIcon className="h-5 w-5" />}
              title={t("landing.hero_feature_4_title")}
              desc={t("landing.hero_feature_4_desc")}
            />
          </div>

          {/* CTAs */}
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <button
              onClick={() => goCheckout("completo")}
              className="lg-cta inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-[1rem]"
            >
              {t("landing.hero_cta_primary")}
              <ArrowRightIcon className="h-4 w-4" />
            </button>
            <a
              href="#how-it-works"
              className="lg-ghost inline-flex items-center gap-2 rounded-xl px-5 py-3.5 text-[0.95rem] font-medium"
            >
              <PlayCircleIcon className="h-5 w-5" style={{ color: "var(--lg-green)" }} />
              {t("landing.hero_cta_secondary")}
            </a>
          </div>

          {/* Trust line */}
          <div className="flex flex-wrap items-center gap-4 text-[0.82rem]" style={{ color: "var(--lg-muted)" }}>
            <TrustDot /> {t("landing.trust_no_commitment")}
            <TrustDot /> {t("landing.trust_no_bureaucracy")}
            <TrustDot /> {t("landing.trust_minutes")}
          </div>
        </div>

        {/* ===== RIGHT: MOCKUP (Dashboard + Phone) ===== */}
        <div className="lg-reveal lg-reveal-d3 relative">
          <DashboardMockup t={t} />
          <PhoneMockup />
          {/* Tooltip callout */}
          <div
            className="absolute -left-2 bottom-8 hidden max-w-[220px] rounded-2xl border px-4 py-3 text-[0.78rem] leading-snug shadow-2xl sm:block"
            style={{
              background: "rgba(12, 22, 40, 0.95)",
              borderColor: "rgba(0, 200, 83, 0.35)",
              color: "var(--lg-text-2)",
            }}
          >
            {t("landing.hero_mockup_tooltip")}
            <span
              className="absolute -right-2 bottom-4 h-3 w-3 rotate-45"
              style={{
                background: "rgba(12, 22, 40, 0.95)",
                borderRight: "1px solid rgba(0, 200, 83, 0.35)",
                borderTop: "1px solid rgba(0, 200, 83, 0.35)",
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniFeature({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl p-2.5">
      <div
        className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
        style={{
          background: "rgba(0, 200, 83, 0.12)",
          color: "var(--lg-green)",
        }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[0.85rem] font-bold" style={{ color: "var(--lg-text)" }}>
          {title}
        </div>
        <div className="text-[0.76rem] leading-snug" style={{ color: "var(--lg-muted)" }}>
          {desc}
        </div>
      </div>
    </div>
  );
}

function TrustDot() {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--lg-green)" }} />
    </span>
  );
}

// ==================== DASHBOARD MOCKUP ====================

function DashboardMockup({ t }: { t: (k: string) => string }) {
  const rows = [
    { name: "João Silva", value: "R$ 1.250,00", status: t("landing.mock_status_sent"), color: "#00C853" },
    { name: "Maria Santos", value: "R$ 2.300,00", status: t("landing.mock_status_reminded"), color: "#FBBF24" },
    { name: "Pedro Oliveira", value: "R$ 980,00", status: t("landing.mock_status_negotiating"), color: "#FB923C" },
    { name: "Ana Costa", value: "R$ 1.750,00", status: t("landing.mock_status_paid"), color: "#00C853" },
  ];

  return (
    <div
      className="relative overflow-hidden rounded-[1.35rem] border shadow-2xl"
      style={{
        background: "linear-gradient(180deg, #0F1B31 0%, #0A1324 100%)",
        borderColor: "var(--lg-border-strong)",
        boxShadow: "0 40px 80px -30px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(148, 163, 184, 0.08) inset",
      }}
    >
      <div className="grid grid-cols-[150px_1fr] sm:grid-cols-[180px_1fr]">
        {/* Sidebar */}
        <aside
          className="flex flex-col gap-0.5 border-r p-3"
          style={{ borderColor: "rgba(148, 163, 184, 0.08)" }}
        >
          <div className="mb-3 flex items-center gap-2 px-2 py-2">
            <LogoMark />
            <span className="lg-display text-[0.95rem] font-bold" style={{ color: "var(--lg-text)" }}>
              Credix
            </span>
          </div>
          <MockMenuItem icon="dash" label="Dashboard" active />
          <MockMenuItem icon="list" label="Cobranças" />
          <MockMenuItem icon="user" label="Clientes" />
          <MockMenuItem icon="money" label="Recebimentos" />
          <MockMenuItem icon="chart" label="Relatórios" />
          <MockMenuItem icon="gear" label="Configurações" />
        </aside>

        {/* Main */}
        <div className="min-w-0 p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="lg-display text-[1.05rem] font-bold" style={{ color: "var(--lg-text)" }}>
              {t("landing.mock_overview")}
            </h3>
            <span
              className="flex items-center gap-1.5 text-[0.68rem] font-medium"
              style={{ color: "var(--lg-muted)" }}
            >
              <span className="lg-live-dot h-1.5 w-1.5 rounded-full" style={{ background: "var(--lg-green)" }} />
              Ao vivo
            </span>
          </div>

          {/* Stats row */}
          <div className="mb-5 grid grid-cols-3 gap-2.5">
            <MockStat label={t("landing.mock_active_charges")} value="128" trend="+12%" />
            <MockStat label={t("landing.mock_received_month")} value="R$ 24.580,00" trend="+18%" />
            <MockStat label={t("landing.mock_success_rate")} value="94%" trend="+8%" />
          </div>

          {/* Table */}
          <div
            className="rounded-xl border p-3"
            style={{ background: "rgba(8, 16, 31, 0.6)", borderColor: "rgba(148, 163, 184, 0.08)" }}
          >
            <div className="mb-2 text-[0.78rem] font-semibold" style={{ color: "var(--lg-text)" }}>
              {t("landing.mock_ongoing")}
            </div>
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-2 text-[0.72rem]">
              <span style={{ color: "var(--lg-muted)" }}>Cliente</span>
              <span className="text-right" style={{ color: "var(--lg-muted)" }}>
                Quantia
              </span>
              <span className="text-right" style={{ color: "var(--lg-muted)" }}>
                Tempo
              </span>
              {rows.map((r, i) => (
                <ContentRow key={i} {...r} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContentRow(props: { name: string; value: string; status: string; color: string }) {
  return (
    <>
      <span className="truncate" style={{ color: "var(--lg-text)" }}>
        {props.name}
      </span>
      <span className="lg-mono text-right font-semibold" style={{ color: "var(--lg-text)" }}>
        {props.value}
      </span>
      <span
        className="justify-self-end rounded-full px-2 py-0.5 text-[0.65rem] font-semibold"
        style={{ background: `${props.color}15`, color: props.color }}
      >
        {props.status}
      </span>
    </>
  );
}

function MockStat({ label, value, trend }: { label: string; value: string; trend: string }) {
  return (
    <div
      className="rounded-xl border p-3"
      style={{
        background: "rgba(8, 16, 31, 0.6)",
        borderColor: "rgba(148, 163, 184, 0.08)",
      }}
    >
      <div className="text-[0.66rem] uppercase tracking-wider" style={{ color: "var(--lg-muted)" }}>
        {label}
      </div>
      <div className="lg-mono mt-1 text-[1.02rem] font-bold" style={{ color: "var(--lg-text)" }}>
        {value}
      </div>
      <div className="mt-0.5 text-[0.66rem] font-medium" style={{ color: "var(--lg-green)" }}>
        {trend} este mês
      </div>
    </div>
  );
}

function MockMenuItem({ icon, label, active = false }: { icon: string; label: string; active?: boolean }) {
  const icons: Record<string, JSX.Element> = {
    dash: <DashIcon />,
    list: <ListIcon />,
    user: <UsersSmallIcon />,
    money: <MoneyIcon />,
    chart: <ChartIcon />,
    gear: <GearIcon />,
  };
  return (
    <div
      className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[0.78rem]"
      style={{
        background: active ? "rgba(0, 200, 83, 0.12)" : "transparent",
        color: active ? "var(--lg-green)" : "var(--lg-text-2)",
        border: active ? "1px solid rgba(0, 200, 83, 0.2)" : "1px solid transparent",
      }}
    >
      <span className="h-3.5 w-3.5">{icons[icon]}</span>
      <span>{label}</span>
    </div>
  );
}

// ==================== PHONE MOCKUP ====================

function PhoneMockup() {
  return (
    <div
      className="absolute -bottom-2 right-2 hidden w-[220px] overflow-hidden rounded-[2.2rem] sm:block lg:w-[252px]"
      style={{
        background: "linear-gradient(180deg, #1E293B 0%, #0F172A 100%)",
        padding: "8px",
        boxShadow:
          "0 30px 60px -20px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(148, 163, 184, 0.25), inset 0 1px 0 rgba(255,255,255,0.1)",
      }}
    >
      <div
        className="overflow-hidden rounded-[1.6rem]"
        style={{ background: "#0A1324" }}
      >
        {/* Status bar + notch */}
        <div className="flex h-6 items-center justify-between px-5 text-[0.6rem]" style={{ color: "var(--lg-text-2)" }}>
          <span className="font-semibold">13:21</span>
          <div
            className="h-3 w-16 rounded-full"
            style={{ background: "#000" }}
          />
          <div className="flex items-center gap-1">
            <SignalIcon />
            <BatteryIcon />
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-4 pt-2">
          {/* Greeting */}
          <div className="mb-3">
            <div className="text-[0.72rem] font-semibold" style={{ color: "var(--lg-text)" }}>
              Olá, João! 👋
            </div>
            <div className="text-[0.62rem]" style={{ color: "var(--lg-muted)" }}>
              Aqui está seu resumo
            </div>
          </div>

          {/* Next payment card */}
          <div
            className="mb-3 rounded-2xl p-3"
            style={{
              background: "linear-gradient(180deg, rgba(0, 200, 83, 0.10) 0%, rgba(0, 200, 83, 0.04) 100%)",
              border: "1px solid rgba(0, 200, 83, 0.22)",
            }}
          >
            <div className="text-[0.58rem] uppercase tracking-wider" style={{ color: "var(--lg-muted)" }}>
              Próxima parcela
            </div>
            <div className="lg-mono mt-0.5 text-[1.15rem] font-extrabold" style={{ color: "var(--lg-text)" }}>
              R$ 450,00
            </div>
            <div className="text-[0.6rem]" style={{ color: "var(--lg-muted)" }}>
              Vence em 15/06/2024
            </div>
            <button
              className="mt-2 w-full rounded-lg py-1.5 text-[0.68rem] font-bold"
              style={{
                background: "linear-gradient(180deg, #00E676, #00C853)",
                color: "#0A0F1A",
              }}
            >
              Pagar agora
            </button>
          </div>

          {/* Installments list */}
          <div className="mb-3 text-[0.6rem] font-semibold" style={{ color: "var(--lg-text)" }}>
            Suas parcelas
          </div>
          <div className="space-y-1.5">
            <InstallmentRow date="15/05/2024" value="R$ 450,00" status="pago" />
            <InstallmentRow date="15/06/2024" value="R$ 450,00" status="pendente" />
            <InstallmentRow date="15/07/2024" value="R$ 450,00" status="futuro" />
          </div>
        </div>

        {/* Tab bar */}
        <div
          className="flex justify-around border-t pb-1.5 pt-2"
          style={{ borderColor: "rgba(148, 163, 184, 0.1)" }}
        >
          {[
            { icon: "home", label: "Início", active: true },
            { icon: "list", label: "Parcelas" },
            { icon: "money", label: "Pagar" },
            { icon: "user", label: "Perfil" },
          ].map((tab) => (
            <div key={tab.label} className="flex flex-col items-center gap-0.5">
              <div
                className="h-3.5 w-3.5"
                style={{ color: tab.active ? "var(--lg-green)" : "var(--lg-muted)" }}
              >
                {tab.icon === "home" && <HomeIcon />}
                {tab.icon === "list" && <ListIcon />}
                {tab.icon === "money" && <MoneyIcon />}
                {tab.icon === "user" && <UsersSmallIcon />}
              </div>
              <span
                className="text-[0.52rem]"
                style={{ color: tab.active ? "var(--lg-green)" : "var(--lg-muted)" }}
              >
                {tab.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InstallmentRow({ date, value, status }: { date: string; value: string; status: "pago" | "pendente" | "futuro" }) {
  const statusConfig = {
    pago: { label: "Pago", color: "#00C853" },
    pendente: { label: "Pendente", color: "#FBBF24" },
    futuro: { label: "Futuro", color: "#94A3B8" },
  }[status];
  return (
    <div
      className="flex items-center justify-between rounded-lg border px-2 py-1.5"
      style={{
        background: "rgba(8, 16, 31, 0.6)",
        borderColor: "rgba(148, 163, 184, 0.08)",
      }}
    >
      <span className="text-[0.62rem]" style={{ color: "var(--lg-muted)" }}>
        {date}
      </span>
      <div className="flex items-center gap-1.5">
        <span className="lg-mono text-[0.68rem] font-semibold" style={{ color: "var(--lg-text)" }}>
          {value}
        </span>
        <span
          className="rounded-full px-1.5 py-px text-[0.52rem] font-semibold"
          style={{ background: `${statusConfig.color}18`, color: statusConfig.color }}
        >
          {statusConfig.label}
        </span>
      </div>
    </div>
  );
}

// ==================== STEPS / SIMPLES ASSIM ====================

function StepsSection({ t }: { t: (k: string) => string }) {
  return (
    <section
      id="how-it-works"
      className="relative"
      style={{
        background: "linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)",
        color: "#1E293B",
      }}
    >
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
        {/* Heading */}
        <div className="mx-auto mb-14 max-w-3xl text-center lg-reveal">
          <div
            className="mb-4 text-[0.8rem] font-bold uppercase tracking-[0.22em]"
            style={{ color: "var(--lg-green)" }}
          >
            {t("landing.steps_eyebrow")}
          </div>
          <h2 className="lg-display text-[2rem] font-extrabold leading-tight sm:text-[2.75rem]">
            {t("landing.steps_title_part1")}
            <br />
            {t("landing.steps_title_part2")}{" "}
            <span style={{ color: "var(--lg-green)" }}>{t("landing.steps_title_highlight")}</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[1rem]" style={{ color: "#64748B" }}>
            {t("landing.steps_subtitle")}
          </p>
        </div>

        {/* 3 steps with arrows */}
        <div className="mb-10 grid items-start gap-5 lg:grid-cols-[1fr_auto_1fr_auto_1fr]">
          <StepCard
            number={1}
            title={t("landing.step_card_1_title")}
            desc={t("landing.step_card_1_desc")}
            illustration={<StepIllustrationCharge />}
          />
          <div className="hidden items-center justify-center lg:flex">
            <ChevronRightBig />
          </div>
          <StepCard
            number={2}
            title={t("landing.step_card_2_title")}
            desc={t("landing.step_card_2_desc")}
            illustration={<StepIllustrationRobot />}
          />
          <div className="hidden items-center justify-center lg:flex">
            <ChevronRightBig />
          </div>
          <StepCard
            number={3}
            title={t("landing.step_card_3_title")}
            desc={t("landing.step_card_3_desc")}
            illustration={<StepIllustrationCheck />}
          />
        </div>

        {/* 5 feature pills */}
        <div
          className="grid gap-3 rounded-2xl border bg-white px-4 py-5 shadow-sm sm:grid-cols-2 lg:grid-cols-5 lg:px-6"
          style={{ borderColor: "#E2E8F0" }}
        >
          <FeaturePill
            icon={<SendIcon />}
            title={t("landing.feature_pill_1_title")}
            desc={t("landing.feature_pill_1_desc")}
          />
          <FeaturePill
            icon={<BellIcon />}
            title={t("landing.feature_pill_2_title")}
            desc={t("landing.feature_pill_2_desc")}
          />
          <FeaturePill
            icon={<ChatIcon />}
            title={t("landing.feature_pill_3_title")}
            desc={t("landing.feature_pill_3_desc")}
          />
          <FeaturePill
            icon={<EyeIcon />}
            title={t("landing.feature_pill_4_title")}
            desc={t("landing.feature_pill_4_desc")}
          />
          <FeaturePill
            icon={<VaultIcon />}
            title={t("landing.feature_pill_5_title")}
            desc={t("landing.feature_pill_5_desc")}
          />
        </div>
      </div>
    </section>
  );
}

function StepCard({
  number,
  title,
  desc,
  illustration,
}: {
  number: number;
  title: string;
  desc: string;
  illustration: React.ReactNode;
}) {
  return (
    <div
      className="relative flex min-h-[180px] items-start justify-between gap-4 rounded-2xl border bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
      style={{ borderColor: "#E2E8F0" }}
    >
      <div className="min-w-0 flex-1">
        <div className="mb-3 flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-full text-[0.95rem] font-bold"
            style={{
              background: "linear-gradient(135deg, #00E676, #00A844)",
              color: "#0A0F1A",
              boxShadow: "0 4px 14px -4px rgba(0, 200, 83, 0.5)",
            }}
          >
            {number}
          </div>
          <h3 className="lg-display text-[1.05rem] font-bold" style={{ color: "#0F172A" }}>
            {title}
          </h3>
        </div>
        <p className="text-[0.88rem] leading-relaxed" style={{ color: "#475569" }}>
          {desc}
        </p>
      </div>
      <div className="shrink-0 opacity-90">{illustration}</div>
    </div>
  );
}

function FeaturePill({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
        style={{
          background: "rgba(0, 200, 83, 0.10)",
          color: "var(--lg-green)",
        }}
      >
        {icon}
      </div>
      <div>
        <div className="text-[0.82rem] font-bold" style={{ color: "#0F172A" }}>
          {title}
        </div>
        <div className="text-[0.73rem]" style={{ color: "#64748B" }}>
          {desc}
        </div>
      </div>
    </div>
  );
}

// ==================== RESULTS (stats + testimonial) ====================

function ResultsSection({ t }: { t: (k: string) => string }) {
  return (
    <section id="benefits" className="relative" style={{ background: "var(--lg-bg)" }}>
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 lg:grid-cols-2 lg:gap-14 lg:px-10 lg:py-28">
        {/* Left: Stats */}
        <div>
          <h2 className="lg-display mb-2 text-[2rem] font-extrabold leading-tight sm:text-[2.4rem]" style={{ color: "var(--lg-text)" }}>
            {t("landing.results_title_part1")}
          </h2>
          <p className="lg-display mb-10 text-[1.5rem] font-semibold sm:text-[1.75rem]" style={{ color: "var(--lg-green)" }}>
            {t("landing.results_title_part2")}
          </p>

          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              icon={<UsersBigIcon />}
              value={t("landing.stat_1_value")}
              label={t("landing.stat_1_label")}
            />
            <StatCard
              icon={<TrendUpIcon />}
              value={t("landing.stat_2_value")}
              label={t("landing.stat_2_label")}
            />
            <StatCard
              icon={<SmileIcon />}
              value={t("landing.stat_3_value")}
              label={t("landing.stat_3_label")}
            />
          </div>
        </div>

        {/* Right: Testimonial */}
        <div
          id="testimonials"
          className="lg-card relative flex flex-col rounded-2xl p-7"
        >
          <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full" style={{ background: "rgba(0, 200, 83, 0.12)" }}>
            <QuoteIcon />
          </div>
          <p className="mb-6 flex-1 text-[1.02rem] leading-relaxed" style={{ color: "var(--lg-text)" }}>
            "{t("landing.testimonial_featured_quote")}"
          </p>
          <div className="mb-3 text-[0.9rem]" style={{ color: "var(--lg-text-2)" }}>
            —{" "}
            <span className="font-semibold" style={{ color: "var(--lg-text)" }}>
              {t("landing.testimonial_featured_name")}
            </span>
            , {t("landing.testimonial_featured_role")}
          </div>
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <StarIcon key={i} className="h-5 w-5" style={{ color: "var(--lg-green)" }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="lg-card rounded-xl p-5">
      <div
        className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg"
        style={{ background: "rgba(0, 200, 83, 0.14)", color: "var(--lg-green)" }}
      >
        {icon}
      </div>
      <div className="lg-display text-[1.75rem] font-extrabold leading-none" style={{ color: "var(--lg-text)" }}>
        {value}
      </div>
      <div className="mt-1.5 text-[0.8rem]" style={{ color: "var(--lg-muted)" }}>
        {label}
      </div>
    </div>
  );
}

// ==================== PRICING ====================

function PricingSection({ t, goCheckout }: { t: (k: string) => string; goCheckout: (plan: string) => void }) {
  const plans: PricingPlanData[] = [
    {
      slug: "essencial",
      name: t("landing.plan_essential_name"),
      subtitle: t("landing.plan_essential_subtitle"),
      price: t("landing.plan_essential_price"),
      priceNote: t("landing.plan_essential_price_note"),
      cta: t("landing.plan_essential_cta"),
      featured: false,
      features: [
        { key: "landing.plan_essential_f1" },
        { key: "landing.plan_essential_f2" },
        { key: "landing.plan_essential_f3" },
        { key: "landing.plan_essential_f4" },
        { key: "landing.plan_essential_f5" },
        { key: "landing.plan_essential_f6", highlight: true },
      ],
    },
    {
      slug: "profissional",
      name: t("landing.plan_pro_name"),
      subtitle: t("landing.plan_pro_subtitle"),
      price: t("landing.plan_pro_price"),
      priceNote: t("landing.plan_pro_price_note"),
      badge: t("landing.plan_pro_badge"),
      cta: t("landing.plan_pro_cta"),
      featured: true,
      features: [
        { key: "landing.plan_pro_f1" },
        { key: "landing.plan_pro_f2" },
        { key: "landing.plan_pro_f3" },
        { key: "landing.plan_pro_f4" },
        { key: "landing.plan_pro_f5" },
        { key: "landing.plan_pro_f6", highlight: true },
        { key: "landing.plan_pro_f7", highlight: true },
      ],
    },
    {
      slug: "enterprise",
      name: t("landing.plan_ent_name"),
      subtitle: t("landing.plan_ent_subtitle"),
      price: t("landing.plan_ent_price"),
      priceNote: t("landing.plan_ent_price_note"),
      cta: t("landing.plan_ent_cta"),
      featured: false,
      features: [
        { key: "landing.plan_ent_f1" },
        { key: "landing.plan_ent_f2" },
        { key: "landing.plan_ent_f3" },
        { key: "landing.plan_ent_f4" },
        { key: "landing.plan_ent_f5" },
        { key: "landing.plan_ent_f6", highlight: true },
      ],
    },
  ];

  return (
    <section
      id="pricing"
      className="relative"
      style={{
        background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)",
        color: "#0F172A",
      }}
    >
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
        {/* Heading */}
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <div
            className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[0.78rem] font-semibold"
            style={{
              background: "rgba(0, 200, 83, 0.08)",
              borderColor: "rgba(0, 200, 83, 0.25)",
              color: "#059669",
            }}
          >
            <ZapIcon className="h-3.5 w-3.5" />
            {t("landing.pricing_eyebrow")}
          </div>

          <h2 className="lg-display mt-6 text-[2.2rem] font-extrabold leading-[1.05] tracking-tight sm:text-[3rem]">
            {t("landing.pricing_headline_part1")}
            <span className="block" style={{ color: "var(--lg-green)" }}>
              {t("landing.pricing_headline_highlight")}
            </span>
          </h2>

          <p className="mx-auto mt-6 max-w-2xl text-[1.05rem] leading-relaxed" style={{ color: "#475569" }}>
            {t("landing.pricing_description_part1")}{" "}
            <span className="font-semibold" style={{ color: "#0F172A" }}>
              {t("landing.pricing_description_highlight")}
            </span>
          </p>
        </div>

        {/* Plans */}
        <div className="grid gap-6 lg:grid-cols-3">
          {plans.map((plan) => (
            <PricingCard key={plan.slug} plan={plan} t={t} onPick={goCheckout} />
          ))}
        </div>

        {/* Conclusion card */}
        <div
          className="mt-16 rounded-[2rem] border p-7 lg:p-10"
          style={{
            background: "linear-gradient(180deg, #ECFDF5 0%, #F0FDF4 100%)",
            borderColor: "#BBF7D0",
          }}
        >
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
            <div>
              <p className="text-[0.78rem] font-bold uppercase tracking-[0.22em]" style={{ color: "#047857" }}>
                {t("landing.pricing_conclusion_eyebrow")}
              </p>

              <h3 className="lg-display mt-3 text-[1.65rem] font-bold leading-tight tracking-tight sm:text-[2rem]">
                {t("landing.pricing_conclusion_title_part1")}
                <span className="block" style={{ color: "var(--lg-green)" }}>
                  {t("landing.pricing_conclusion_title_highlight")}
                </span>
              </h3>

              <p className="mt-4 max-w-2xl text-[0.95rem] leading-relaxed" style={{ color: "#334155" }}>
                {t("landing.pricing_conclusion_body")}
              </p>

              <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
                {[
                  "landing.pricing_conclusion_point1",
                  "landing.pricing_conclusion_point2",
                  "landing.pricing_conclusion_point3",
                  "landing.pricing_conclusion_point4",
                ].map((k) => (
                  <div
                    key={k}
                    className="flex items-center gap-2.5 rounded-xl bg-white px-3.5 py-2.5 text-[0.82rem] font-medium"
                    style={{ color: "#1E293B", border: "1px solid #D1FAE5" }}
                  >
                    <CheckCircleFillIcon className="h-4 w-4 shrink-0" style={{ color: "var(--lg-green)" }} />
                    {t(k)}
                  </div>
                ))}
              </div>
            </div>

            <div
              className="rounded-[1.5rem] p-7"
              style={{
                background: "#06101F",
                color: "#F8FAFC",
                boxShadow: "0 30px 60px -20px rgba(6, 16, 31, 0.35)",
              }}
            >
              <p className="text-[0.72rem] font-bold uppercase tracking-[0.22em]" style={{ color: "#4ADE80" }}>
                {t("landing.pricing_conclusion_side_eyebrow")}
              </p>
              <h4 className="lg-display mt-4 text-[1.35rem] font-bold leading-tight">
                {t("landing.pricing_conclusion_side_title")}
              </h4>
              <p className="mt-3 text-[0.9rem] leading-relaxed" style={{ color: "#CBD5E1" }}>
                {t("landing.pricing_conclusion_side_body")}
              </p>
              <button
                onClick={() => goCheckout("profissional")}
                className="lg-cta mt-5 inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-[0.9rem]"
              >
                {t("landing.pricing_conclusion_side_cta")}
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

interface PricingPlanData {
  slug: string;
  name: string;
  subtitle: string;
  price: string;
  priceNote: string;
  badge?: string;
  cta: string;
  featured: boolean;
  features: { key: string; highlight?: boolean }[];
}

function PricingCard({
  plan,
  t,
  onPick,
}: {
  plan: PricingPlanData;
  t: (k: string) => string;
  onPick: (slug: string) => void;
}) {
  const featured = plan.featured;
  return (
    <div
      className={`relative flex flex-col rounded-3xl border p-7 shadow-sm transition-transform duration-300 lg:p-8 ${
        featured ? "lg:-translate-y-2" : "hover:-translate-y-1"
      }`}
      style={
        featured
          ? {
              background: "linear-gradient(180deg, #0B1628 0%, #06101F 100%)",
              borderColor: "rgba(0, 200, 83, 0.5)",
              color: "#F8FAFC",
              boxShadow:
                "0 30px 60px -20px rgba(0, 200, 83, 0.22), 0 0 0 1px rgba(0, 200, 83, 0.08) inset",
            }
          : { background: "#FFFFFF", borderColor: "#E2E8F0", color: "#0F172A" }
      }
    >
      {plan.badge && (
        <div
          className="absolute -top-3 left-7 rounded-full px-3.5 py-1 text-[0.72rem] font-bold uppercase tracking-wider"
          style={{
            background: "linear-gradient(180deg, #00E676, #00C853)",
            color: "#0A0F1A",
            boxShadow: "0 8px 18px -4px rgba(0, 200, 83, 0.55)",
          }}
        >
          {plan.badge}
        </div>
      )}

      <div className="mb-5">
        <h3 className="lg-display text-[1.5rem] font-bold">{plan.name}</h3>
        <p
          className="mt-2 text-[0.88rem] leading-relaxed"
          style={{ color: featured ? "#CBD5E1" : "#64748B" }}
        >
          {plan.subtitle}
        </p>
      </div>

      <div className="mb-6">
        <div className="lg-display text-[2rem] font-extrabold leading-none tracking-tight">{plan.price}</div>
        <p className="mt-2 text-[0.82rem]" style={{ color: featured ? "#94A3B8" : "#64748B" }}>
          {plan.priceNote}
        </p>
      </div>

      <button
        onClick={() => {
          if (plan.slug === "enterprise") {
            // Enterprise e sales-led: abre WhatsApp em vez de checkout
            window.open(
              "https://wa.me/5511999999999?text=" +
                encodeURIComponent("Oi! Quero saber mais sobre o plano Enterprise da Credix."),
              "_blank",
              "noopener,noreferrer"
            );
            return;
          }
          onPick(plan.slug);
        }}
        className="group inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-[0.92rem] font-semibold transition-all"
        style={
          featured
            ? {
                background: "linear-gradient(180deg, #00E676, #00C853)",
                color: "#0A0F1A",
                boxShadow:
                  "0 10px 25px -8px rgba(0, 200, 83, 0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
              }
            : { background: "#0F172A", color: "#FFFFFF" }
        }
      >
        {plan.cta}
        <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </button>

      <div
        className="my-7 h-px w-full"
        style={{ background: featured ? "rgba(148,163,184,0.15)" : "#E2E8F0" }}
      />

      <ul className="space-y-3.5">
        {plan.features.map((f) => (
          <li key={f.key} className="flex items-start gap-3">
            <span
              className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
              style={
                f.highlight
                  ? { background: "var(--lg-green)", color: "#0A0F1A" }
                  : featured
                  ? { background: "rgba(255,255,255,0.08)", color: "#4ADE80" }
                  : { background: "#ECFDF5", color: "#059669" }
              }
            >
              <CheckIcon className="h-3 w-3" />
            </span>
            <span
              className="text-[0.88rem] leading-relaxed"
              style={{
                color: f.highlight
                  ? featured
                    ? "#FFFFFF"
                    : "#0F172A"
                  : featured
                  ? "#CBD5E1"
                  : "#475569",
                fontWeight: f.highlight ? 600 : 400,
              }}
            >
              {t(f.key)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ZapIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

// ==================== FAQ ====================

function FaqSection({
  t,
  openFaq,
  setOpenFaq,
}: {
  t: (k: string) => string;
  openFaq: number | null;
  setOpenFaq: (n: number | null) => void;
}) {
  return (
    <section
      id="faq"
      className="relative"
      style={{ background: "var(--lg-surface)", borderTop: "1px solid var(--lg-border)" }}
    >
      <div className="mx-auto max-w-3xl px-5 py-20 lg:px-10 lg:py-28">
        <div className="mx-auto mb-10 text-center">
          <h2 className="lg-display text-[2rem] font-extrabold leading-tight sm:text-[2.5rem]" style={{ color: "var(--lg-text)" }}>
            {t("landing.faq_title")}
          </h2>
        </div>

        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <FaqItem
              key={i}
              question={t(`landing.faq_${i}_q`)}
              answer={t(`landing.faq_${i}_a`)}
              open={openFaq === i}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqItem({
  question,
  answer,
  open,
  onClick,
}: {
  question: string;
  answer: string;
  open: boolean;
  onClick: () => void;
}) {
  return (
    <div
      className="overflow-hidden rounded-xl border transition-colors"
      style={{
        background: "var(--lg-bg)",
        borderColor: open ? "rgba(0, 200, 83, 0.35)" : "var(--lg-border)",
      }}
    >
      <button
        onClick={onClick}
        className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left"
      >
        <span className="text-[0.98rem] font-semibold" style={{ color: "var(--lg-text)" }}>
          {question}
        </span>
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-transform"
          style={{
            background: open ? "rgba(0, 200, 83, 0.15)" : "rgba(148, 163, 184, 0.08)",
            color: open ? "var(--lg-green)" : "var(--lg-muted)",
            transform: open ? "rotate(45deg)" : "rotate(0)",
          }}
        >
          <PlusSmallIcon />
        </span>
      </button>
      <div
        className="grid overflow-hidden transition-all duration-300"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <p className="px-6 pb-5 text-[0.92rem] leading-relaxed" style={{ color: "var(--lg-text-2)" }}>
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}

// ==================== FINAL CTA ====================

function FinalCtaSection({ t, goCheckout }: { t: (k: string) => string; goCheckout: (plan: string) => void }) {
  return (
    <section className="relative" style={{ background: "var(--lg-bg)", borderTop: "1px solid var(--lg-border)" }}>
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 lg:grid-cols-[1.4fr_1fr] lg:px-10 lg:py-28">
        <div>
          <h2 className="lg-display mb-1 text-[2rem] font-extrabold leading-tight sm:text-[2.6rem]" style={{ color: "var(--lg-text)" }}>
            {t("landing.final_title_part1")}
          </h2>
          <h2 className="lg-display mb-6 text-[2rem] font-extrabold leading-tight sm:text-[2.6rem]" style={{ color: "var(--lg-green)" }}>
            {t("landing.final_title_highlight")}
          </h2>
          <p className="mb-8 max-w-xl text-[1rem] leading-relaxed" style={{ color: "var(--lg-text-2)" }}>
            {t("landing.final_description")}
          </p>

          <div className="mb-5 flex flex-wrap items-center gap-3">
            <button
              onClick={() => goCheckout("completo")}
              className="lg-cta inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-[0.98rem]"
            >
              {t("landing.final_cta_primary")}
              <ArrowRightIcon className="h-4 w-4" />
            </button>
            <a
              href="https://wa.me/5511999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="lg-ghost inline-flex items-center gap-2 rounded-xl px-5 py-3.5 text-[0.94rem] font-medium"
            >
              {t("landing.final_cta_secondary")}
              <WhatsAppIcon className="h-5 w-5" style={{ color: "var(--lg-green)" }} />
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-[0.82rem]" style={{ color: "var(--lg-muted)" }}>
            <CheckSmallIcon /> {t("landing.final_trust_1")}
            <CheckSmallIcon /> {t("landing.final_trust_2")}
            <CheckSmallIcon /> {t("landing.final_trust_3")}
          </div>
        </div>

        {/* Security card */}
        <div className="lg-card rounded-2xl p-6">
          <div className="mb-4 flex items-start gap-3">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
              style={{ background: "rgba(0, 200, 83, 0.14)", color: "var(--lg-green)" }}
            >
              <ShieldBigIcon />
            </div>
            <div>
              <h3 className="lg-display text-[1.08rem] font-bold" style={{ color: "var(--lg-text)" }}>
                {t("landing.security_title")}
              </h3>
              <p className="mt-1 text-[0.85rem] leading-relaxed" style={{ color: "var(--lg-text-2)" }}>
                {t("landing.security_desc")}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <SecurityBadge icon={<LockIcon />} title="LGPD" subtitle="Compliant" />
            <SecurityBadge icon={<KeyIcon />} title="Dados 100%" subtitle="Seguros" />
          </div>
        </div>
      </div>
    </section>
  );
}

function SecurityBadge({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div
      className="flex items-center gap-2.5 rounded-xl border px-3 py-2.5"
      style={{
        background: "rgba(8, 16, 31, 0.5)",
        borderColor: "rgba(148, 163, 184, 0.14)",
      }}
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-full" style={{ background: "rgba(0, 200, 83, 0.14)", color: "var(--lg-green)" }}>
        {icon}
      </span>
      <div>
        <div className="text-[0.8rem] font-bold" style={{ color: "var(--lg-text)" }}>
          {title}
        </div>
        <div className="text-[0.7rem]" style={{ color: "var(--lg-muted)" }}>
          {subtitle}
        </div>
      </div>
    </div>
  );
}

// ==================== FOOTER ====================

function Footer({ t }: { t: (k: string) => string }) {
  return (
    <footer style={{ background: "var(--lg-surface)", borderTop: "1px solid var(--lg-border)" }}>
      <div className="mx-auto max-w-7xl px-5 py-14 lg:px-10">
        <div className="grid gap-10 md:grid-cols-4">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2.5">
              <LogoMark />
              <span className="lg-display text-[1.1rem] font-bold" style={{ color: "var(--lg-text)" }}>
                Cred<span style={{ color: "var(--lg-green)" }}>ix</span>
              </span>
            </div>
            <p className="mt-3 text-[0.85rem] leading-relaxed" style={{ color: "var(--lg-muted)" }}>
              {t("landing.footer_desc")}
            </p>
          </div>

          <div>
            <h4 className="mb-3 text-[0.78rem] font-bold uppercase tracking-wider" style={{ color: "var(--lg-text)" }}>
              {t("landing.footer_product")}
            </h4>
            <ul className="space-y-2 text-[0.88rem]" style={{ color: "var(--lg-muted)" }}>
              <li><a href="#features" className="transition-colors hover:text-white">{t("landing.nav_features")}</a></li>
              <li><a href="#pricing" className="transition-colors hover:text-white">{t("landing.nav_pricing")}</a></li>
              <li><a href="#how-it-works" className="transition-colors hover:text-white">{t("landing.nav_how")}</a></li>
              <li><a href="#faq" className="transition-colors hover:text-white">{t("landing.nav_faq")}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="mb-3 text-[0.78rem] font-bold uppercase tracking-wider" style={{ color: "var(--lg-text)" }}>
              {t("landing.footer_company")}
            </h4>
            <ul className="space-y-2 text-[0.88rem]" style={{ color: "var(--lg-muted)" }}>
              <li>{t("landing.footer_about")}</li>
              <li>{t("landing.footer_contact")}</li>
              <li>{t("landing.footer_careers")}</li>
            </ul>
          </div>

          <div>
            <h4 className="mb-3 text-[0.78rem] font-bold uppercase tracking-wider" style={{ color: "var(--lg-text)" }}>
              {t("landing.footer_legal")}
            </h4>
            <ul className="space-y-2 text-[0.88rem]" style={{ color: "var(--lg-muted)" }}>
              <li>{t("landing.footer_privacy")}</li>
              <li>{t("landing.footer_terms")}</li>
              <li>{t("landing.footer_lgpd")}</li>
            </ul>
          </div>
        </div>

        <div
          className="mt-12 border-t pt-6 text-center text-[0.82rem]"
          style={{ borderColor: "var(--lg-border)", color: "var(--lg-muted)" }}
        >
          {t("landing.footer")}
        </div>
      </div>
    </footer>
  );
}

// ==================== ICONS ====================

function ArrowRightIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
  );
}

function CheckIcon({ className = "h-4 w-4", ...rest }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} {...rest} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CheckCircleFillIcon({ className = "h-5 w-5", ...rest }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} {...rest} viewBox="0 0 24 24" fill="currentColor">
      <path fillRule="evenodd" clipRule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM16.7071 9.70711C17.0976 9.31658 17.0976 8.68342 16.7071 8.29289C16.3166 7.90237 15.6834 7.90237 15.2929 8.29289L10.5 13.0858L8.70711 11.2929C8.31658 10.9024 7.68342 10.9024 7.29289 11.2929C6.90237 11.6834 6.90237 12.3166 7.29289 12.7071L9.79289 15.2071C10.1834 15.5976 10.8166 15.5976 11.2071 15.2071L16.7071 9.70711Z" />
    </svg>
  );
}

function CheckSmallIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" style={{ color: "var(--lg-green)" }}>
      <path fillRule="evenodd" clipRule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM16.7071 9.70711C17.0976 9.31658 17.0976 8.68342 16.7071 8.29289C16.3166 7.90237 15.6834 7.90237 15.2929 8.29289L10.5 13.0858L8.70711 11.2929C8.31658 10.9024 7.68342 10.9024 7.29289 11.2929C6.90237 11.6834 6.90237 12.3166 7.29289 12.7071L9.79289 15.2071C10.1834 15.5976 10.8166 15.5976 11.2071 15.2071L16.7071 9.70711Z" />
    </svg>
  );
}

function PlusSmallIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function ShieldCheckIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function ShieldBigIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function TrendDownIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
    </svg>
  );
}

function TrendUpIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );
}

function ClockIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function PhoneIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

function PlayCircleIcon({
  className = "h-5 w-5",
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function QuoteIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" style={{ color: "var(--lg-green)" }}>
      <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
    </svg>
  );
}

function StarIcon({ className = "h-4 w-4", ...rest }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} {...rest} viewBox="0 0 20 20" fill="currentColor">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

function WhatsAppIcon({ className = "h-5 w-5", ...rest }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} {...rest} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
    </svg>
  );
}

function UsersBigIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function SmileIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function VaultIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M10 14h4m-7 4h10a2 2 0 002-2V6a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function ChevronRightBig() {
  return (
    <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="#94A3B8" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

/* ===== Sidebar / tabbar icons ===== */
function DashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}
function ListIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}
function UsersSmallIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
function MoneyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V7m0 10v1m9-8a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function ChartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}
function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}
function SignalIcon() {
  return (
    <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M2 16h3v4H2zM7 12h3v8H7zM12 8h3v12h-3zM17 4h3v16h-3z" />
    </svg>
  );
}
function BatteryIcon() {
  return (
    <svg className="h-2.5 w-4" viewBox="0 0 24 12" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <rect x="1" y="1" width="20" height="10" rx="2" />
      <rect x="3" y="3" width="14" height="6" fill="currentColor" />
      <rect x="22" y="4" width="1.5" height="4" fill="currentColor" />
    </svg>
  );
}

/* ===== Step illustrations ===== */
function StepIllustrationCharge() {
  return (
    <div className="relative flex h-16 w-16 items-center justify-center rounded-xl" style={{ background: "#F1F5F9" }}>
      <svg className="h-9 w-9" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth={1.5}>
        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div
        className="absolute -bottom-1.5 -right-1.5 flex h-6 w-6 items-center justify-center rounded-full text-[0.7rem] font-bold"
        style={{ background: "var(--lg-green)", color: "#0A0F1A" }}
      >
        +
      </div>
    </div>
  );
}

function StepIllustrationRobot() {
  return (
    <div className="flex h-16 w-16 items-center justify-center rounded-xl" style={{ background: "#F0FDF4" }}>
      <svg className="h-10 w-10" viewBox="0 0 32 32" fill="none">
        {/* Antena */}
        <circle cx="16" cy="5" r="1.5" fill="#00C853" />
        <line x1="16" y1="6.5" x2="16" y2="9" stroke="#00C853" strokeWidth="1.5" />
        {/* Head */}
        <rect x="7" y="9" width="18" height="15" rx="3.5" fill="#111827" />
        {/* Eyes */}
        <circle cx="12" cy="16" r="1.8" fill="#00C853" />
        <circle cx="20" cy="16" r="1.8" fill="#00C853" />
        {/* Mouth */}
        <rect x="13" y="20" width="6" height="1.5" rx="0.75" fill="#00C853" />
        {/* Side dots */}
        <circle cx="5" cy="16" r="1" fill="#00C853" />
        <circle cx="27" cy="16" r="1" fill="#00C853" />
      </svg>
    </div>
  );
}

function StepIllustrationCheck() {
  return (
    <div
      className="flex h-16 w-16 items-center justify-center rounded-full"
      style={{
        background: "linear-gradient(135deg, #00E676 0%, #00A844 100%)",
        boxShadow: "0 10px 25px -5px rgba(0, 200, 83, 0.5)",
      }}
    >
      <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="#0A0F1A" strokeWidth={3.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
}
